import { useState } from "react";
import { AlertCircle, Check, Copy, Share2 } from "react-feather";
import { TrackingData } from "@/lib/types";
import { trackingTypeLogos, trackingTypes } from "@/lib/constants";
import { diaFalado, hora, quando } from "@/lib/utils";
import { API_URL } from "@/lib/api";
import MapaDaEntrega from "@/ao-vivo/MapaDaEntrega";

// O topo do rastreio, no molde dos apps de entrega (Amazon, Shop, Mercado
// Livre): a frase do status grande, a trilha de etapas e, embaixo, o código, quem
// entrega e o compartilhar. Na entrega ao vivo acontecendo, o mapa do
// motorista abre no alto do mesmo cartão (iFood, Uber): é o que a pessoa veio
// ver. Mesmo bloco pra transportadora e ao vivo, só mudam os textos.

type Tom = "andamento" | "ok" | "problema";
type Texto = { titulo: string; sub: string; tom: Tom };

const TEXTO: Record<number, Texto> = {
  9: { titulo: "Preparando o envio", sub: "O pacote ainda vai ser postado.", tom: "andamento" },
  1: { titulo: "Em trânsito", sub: "O pacote está a caminho do endereço.", tom: "andamento" },
  6: { titulo: "Saiu para entrega", sub: "O pacote está com o entregador e chega em breve.", tom: "andamento" },
  3: { titulo: "Entregue", sub: "", tom: "ok" },
  2: { titulo: "Aguardando retirada", sub: "O pacote está disponível para retirada na unidade da transportadora.", tom: "problema" },
  7: { titulo: "Não foi possível entregar", sub: "A transportadora tentou entregar e não conseguiu. Veja o histórico abaixo.", tom: "problema" },
  4: { titulo: "Em devolução", sub: "O pacote está voltando para o remetente.", tom: "problema" },
  5: { titulo: "Devolvido", sub: "O pacote voltou para o remetente.", tom: "problema" },
  8: { titulo: "Envio cancelado", sub: "Este envio foi cancelado.", tom: "problema" },
};

// Entrega ao vivo fala da entrega e do motorista, não do "pacote".
const TEXTO_AO_VIVO: Record<number, Texto> = {
  9: { titulo: "Entrega agendada", sub: "A rota aparece no mapa quando o motorista sair para o endereço.", tom: "andamento" },
  6: { titulo: "Sua entrega está a caminho", sub: "Acompanhe o motorista no mapa, em tempo real.", tom: "andamento" },
  3: { titulo: "Entregue", sub: "", tom: "ok" },
  8: { titulo: "Entrega cancelada", sub: "Esta entrega foi cancelada.", tom: "problema" },
};

const ETAPAS = ["Preparando", "Em trânsito", "Saiu para entrega", "Entregue"];
const ETAPA_DO_STATUS: Record<number, number> = { 9: 0, 1: 1, 2: 1, 6: 2, 7: 2, 3: 3 };
const ETAPAS_AO_VIVO = ["Agendada", "A caminho", "Entregue"];
const ETAPA_AO_VIVO: Record<number, number> = { 9: 0, 6: 1, 3: 2 };

const COR_DO_TOM: Record<Tom, string> = {
  andamento: "bg-[#FFF1E6] text-[#C2410C]",
  ok: "bg-green-50 text-green-700",
  problema: "bg-red-50 text-red-700",
};

export default function ResumoDoRastreio({ dados }: { dados: TrackingData }) {
  const [copiado, setCopiado] = useState<"codigo" | "link" | null>(null);
  const aoVivo = Boolean(dados.aoVivo);
  const ultimo = dados.events[0];
  let texto = (aoVivo && TEXTO_AO_VIVO[dados.lastStatus]) || TEXTO[dados.lastStatus] || TEXTO[1];
  // Ao vivo, a chegada vem como "saiu para entrega" com outro texto.
  if (aoVivo && dados.lastStatus === 6 && /chegou/i.test(ultimo?.description ?? "")) {
    texto = { titulo: "O motorista chegou", sub: "Ele está no endereço da entrega agora.", tom: "andamento" };
  }
  const entregueEm = dados.lastStatus === 3 ? dados.deliveredAt || ultimo?.createdAt : "";
  const sub = entregueEm ? `Entregue ${diaFalado(entregueEm)} às ${hora(entregueEm)}.` : texto.sub;
  const etapas = aoVivo ? ETAPAS_AO_VIVO : ETAPAS;
  const atual = (aoVivo ? ETAPA_AO_VIVO : ETAPA_DO_STATUS)[dados.lastStatus];
  // Quem entrega: a transportadora de origem; "beuniday" (entrega própria do
  // evento) e tipo desconhecido ficam como beuni, com o logo da beuni.
  const origem = dados.originalTrackingType && dados.originalTrackingType !== "beuniday" ? dados.originalTrackingType : dados.trackingType;
  const quem = origem in trackingTypeLogos ? origem : "beuni";
  // Código na transportadora (envio beuni que foi pelos Correios, por
  // exemplo): pra consultar direto lá ou informar no atendimento.
  const codigoNaTransportadora = !aoVivo && dados.originalTrackingCode && dados.originalTrackingCode !== dados.trackingCode ? dados.originalTrackingCode : "";
  // A chave do acompanhamento (o mapa lê a entrega por ela). Resposta antiga
  // do backend só tem o link do dashboard, que termina na chave.
  const chave = dados.aoVivo?.token || dados.aoVivo?.link?.split("?")[0].split("/").pop() || "";
  const mapa = aoVivo && dados.aoVivo?.ativo && chave;

  const copiar = (oque: "codigo" | "link") => {
    navigator.clipboard.writeText(oque === "codigo" ? dados.trackingCode : window.location.href.split("?")[0]);
    setCopiado(oque);
    setTimeout(() => setCopiado(null), 2000);
  };

  return (
    // No celular o cartão vai de ponta a ponta (mapa na largura toda, como nos
    // apps de entrega); do tablet pra cima vira cartão arredondado.
    <section className="-mx-4 -mt-6 overflow-hidden bg-white shadow-[0_1px_0_#EFE6DD] sm:mx-0 sm:mt-0 sm:rounded-3xl sm:shadow-[0_1px_2px_rgba(63,32,33,0.06),0_8px_24px_rgba(63,32,33,0.06)] sm:ring-1 sm:ring-[#EFE6DD]">
      {mapa && <MapaDaEntrega token={chave} apiUrl={API_URL} />}

      <div className="px-5 pb-6 pt-5 sm:px-8 sm:pt-7">
        <div className="flex items-center justify-between gap-3">
          {mapa ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-[#FFF1E6] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#C2410C]">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#DC4405] opacity-60" />
                <span className="relative inline-flex size-2 rounded-full bg-[#DC4405]" />
              </span>
              {/* "Ao vivo" com o motorista andando; antes de ele sair, "Hoje". */}
              {dados.lastStatus === 9 ? "Hoje" : "Ao vivo"}
            </span>
          ) : (
            // Sem repetir o título: o selo só aparece pra avisar problema ou
            // confirmar a entrega.
            texto.tom !== "andamento" && (
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${COR_DO_TOM[texto.tom]}`}>
                {texto.tom === "ok" ? <Check size={13} strokeWidth={3} /> : <AlertCircle size={13} strokeWidth={2.5} />}
                {texto.tom === "ok" ? "Concluído" : "Atenção"}
              </span>
            )
          )}
          {/* Ao vivo, a hora que vale é a da posição, na linha embaixo do mapa. */}
          {ultimo && !mapa && <span className="ml-auto text-xs text-[#8a7778]">Atualizado {quando(ultimo.createdAt)}</span>}
        </div>

        <h1 className="mt-3 text-[28px] font-bold leading-tight tracking-tight text-[#3F2021] sm:text-[34px]">{texto.titulo}</h1>
        {sub && <p className="mt-1.5 text-[15px] text-[#6b5556] sm:text-base">{sub}</p>}

        {/* Trilha de etapas, a mesma da página do mapa: feitas em marrom,
            a atual em laranja, as próximas vazadas. Devolução e cancelamento
            ficam fora da linha e não mostram trilha. */}
        {atual !== undefined && <Trilha etapas={etapas} atual={atual} entregue={dados.lastStatus === 3} />}
      </div>

      <div className="flex flex-col gap-4 border-t border-[#F1E8DF] bg-[#FCFAF7] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <p className="text-xs text-[#8a7778]">Código de rastreio</p>
            <button
              type="button"
              onClick={() => copiar("codigo")}
              className="mt-0.5 flex items-center gap-1.5 font-mono text-[15px] font-semibold tracking-wide text-[#3F2021] hover:text-primary"
              title="Copiar código"
            >
              {dados.trackingCode}
              {copiado === "codigo" ? <Check size={14} className="text-green-600" /> : <Copy size={14} className="text-[#8a7778]" />}
            </button>
          </div>
          {codigoNaTransportadora && (
            <div>
              <p className="text-xs text-[#8a7778]">Código na transportadora</p>
              <p className="mt-0.5 font-mono text-[15px] font-semibold tracking-wide text-[#3F2021]">{codigoNaTransportadora}</p>
            </div>
          )}
          <div className="flex items-center gap-2.5">
            <img
              src={trackingTypeLogos[quem as keyof typeof trackingTypeLogos]}
              alt=""
              className="size-9 rounded-lg bg-white object-contain p-1 ring-1 ring-[#EFE6DD]"
            />
            <div>
              <p className="text-xs text-[#8a7778]">{aoVivo ? "Entrega" : "Transportadora"}</p>
              <p className="text-sm font-semibold text-[#3F2021]">{trackingTypes[quem as keyof typeof trackingTypes] ?? quem}</p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => copiar("link")}
          className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
            copiado === "link" ? "bg-green-600 text-white" : "bg-[#3F2021] text-white hover:bg-[#2c1617]"
          }`}
        >
          {copiado === "link" ? <Check size={16} /> : <Share2 size={16} />}
          {copiado === "link" ? "Link copiado" : "Compartilhar rastreio"}
        </button>
      </div>
    </section>
  );
}

function Trilha({ etapas, atual, entregue }: { etapas: string[]; atual: number; entregue: boolean }) {
  return (
    <ol className="mt-6 grid" style={{ gridTemplateColumns: `repeat(${etapas.length}, minmax(0, 1fr))` }}>
      {etapas.map((nome, i) => {
        const estado = i < atual ? "feita" : i === atual ? "atual" : "futura";
        return (
          <li key={nome} className="relative flex flex-col items-center text-center">
            {/* Linha até a próxima etapa, atrás das bolinhas. */}
            {i < etapas.length - 1 && (
              <span
                className={`absolute left-1/2 top-[11px] h-[3px] w-full ${i < atual ? "bg-[#3F2021]" : "bg-[#EADFD3]"}`}
                aria-hidden="true"
              />
            )}
            <span
              className={`relative z-10 flex items-center justify-center rounded-full ${
                estado === "atual"
                  ? `size-6 text-white ring-4 ${entregue ? "bg-green-600 ring-green-100" : "bg-[#DC4405] ring-[#FFE3D1]"}`
                  : estado === "feita"
                    ? "mt-1 size-4 bg-[#3F2021]"
                    : "mt-1 size-4 border-2 border-[#D9CBBE] bg-white"
              }`}
            >
              {estado === "atual" && (entregue ? <Check size={14} strokeWidth={3} /> : <span className="size-2 rounded-full bg-white" />)}
            </span>
            <span
              className={`mt-2 px-1 text-xs leading-tight sm:text-[13px] ${
                estado === "atual" ? "font-bold text-[#3F2021]" : estado === "feita" ? "text-[#5b4546]" : "text-[#A8978F]"
              }`}
            >
              {nome}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
